import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { LinkedLogo, NamedLink } from '../../../../components';

import TopbarAccountMenu from '../TopbarAccountMenu/TopbarAccountMenu';
import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import TopbarWishlistLink from '../TopbarWishlistLink/TopbarWishlistLink';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const BecomeSellerLink = () => {
  return (
    <NamedLink name="CMSPage" params={{ pageId: 'how-to-sell' }} className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.becomeSeller" />
      </span>
    </NamedLink>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const becomeSellerLinkMaybe = isAuthenticatedOrJustHydrated ? null : <BecomeSellerLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      /* No className: the pill carries its own size and placement, and no takeAvailableSpace
         either - stretching it to fill the row left a hole inside it once Was was switched off.
         The spacer below still takes that class; a placeholder with nothing in it is exactly
         what should absorb the slack. */
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <LinkedLogo
        className={css.logoLink}
        logoClassName={css.logoWrapper}
        logoImageClassName={css.logoImage}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      {searchFormMaybe}

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        showCreateListingsLink={showCreateListingsLink}
        isAuthenticated={authenticatedOnClientSide}
        isProvider={props.isProvider}
        isCustomer={props.isCustomer}
      />

      {becomeSellerLinkMaybe}

      <TopbarWishlistLink intl={intl} />
      <TopbarAccountMenu
        currentPage={currentPage}
        currentUser={currentUser}
        isAuthenticated={isAuthenticated}
        onLogout={onLogout}
        showManageListingsLink={showCreateListingsLink}
        notificationCount={notificationCount}
        inboxTab={inboxTab}
        intl={intl}
      />
    </nav>
  );
};

export default TopbarDesktop;
